import { WithRouter, withRouter } from 'found';
import React, { useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { compose } from 'redux';
import { fetchCollectionContents, fetchCollections } from '../actions/collections.actions';
import withAsyncData from '../components/utils/withAsyncData';
import { routes } from '../constants';
import { StoreType } from '../reducers';
import { Collection, Pagination } from '../types/AppContent';
import { AsyncActionStatus } from '../actions/common.actionTypes';
import {
  selectCollectionWithContents,
  selectCollectionStatus,
  CollectionTypes
} from '../reducers/collections.selectors';
import {
  addItemToCollection,
  deleteItemFromCollection,
} from '../actions/collections.actions';
import { createPaginationRequest } from '../utils/pagination';
import { ampliTrackCollectionViewedEvent } from '../services/track.service';
import { selectUser } from '../reducers/user.selectors';
import OwnCollectionOverview from '../components/OwnCollectionOverview';

interface StateProps {
  collection?: Collection;
  collectionStatus: AsyncActionStatus;
  collectionPagination: Pagination;
}

interface DispatchProps {
  fetchCollectionContents: typeof fetchCollectionContents;
  addItemToCollection: typeof addItemToCollection;
  deleteItemFromCollection: typeof deleteItemFromCollection;
}

interface OwnProps {
  match: {
    params: {
      collectionId: string;
    };
  };
  router: {
    push: (location: string) => void;
  };
}

type OwnCollectionItemListPageProps = StateProps & DispatchProps & OwnProps & WithRouter;

const OwnCollectionItemListPage: React.FC<OwnCollectionItemListPageProps> = ({
  match,
  collection,
  collectionStatus,
  router,
  ...props
}) => {
  const user = useSelector(selectUser);

  const collectionId = match.params.collectionId;

  if (!collection && collectionStatus === AsyncActionStatus.NOTFOUND) {
    router.replace(`/${routes.collections.root}`);
  }

  useEffect(() => {
    props.fetchCollectionContents(collectionId, createPaginationRequest(props.collectionPagination));
    if (user && collection) {
      ampliTrackCollectionViewedEvent(collection);
    }
  }, [collectionId]);

  if (!collection) {
    return null;
  }

  return (
    <OwnCollectionOverview
      collection={collection}
      collectionStatus={collectionStatus}
      collectionId={collectionId}
      collectionPagination={props.collectionPagination}
      loadNext={() => {
        return props.fetchCollectionContents(collectionId, createPaginationRequest({
          ...props.collectionPagination,
          offset: props.collectionPagination.offset + props.collectionPagination.limit,
        }));
      }}
    />
  );

};

const mapState = (state: StoreType, props: OwnProps): StateProps => ({
  collection: selectCollectionWithContents(state, props.match.params.collectionId, CollectionTypes.OWN),
  collectionStatus: selectCollectionStatus(state, props.match.params.collectionId, CollectionTypes.OWN),
  collectionPagination: state.collections.byId[props.match.params.collectionId] &&
    state.collections.byId[props.match.params.collectionId].pagination,
});

export default compose<React.FC<Record<string,never>>>(
  withRouter,
  connect(
    mapState,
    {
      addItemToCollection,
      deleteItemFromCollection,
      fetchCollections,
      fetchCollectionContents
    },
  ),
  withAsyncData((props) => ({
    refetch: () => props.fetchCollections(),
    dataProp: 'collection',
    statusProp: 'collectionStatus',
  })),
)(OwnCollectionItemListPage);
